import React, { useEffect, useState } from "react";
import { UserCircle, Camera, Link as LinkIcon } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const fmtMonthYearDe = (ym) => {
  const m = /^(\d{4})-(\d{2})$/.exec(ym || "");
  return m ? `${m[2]}/${m[1]}` : (ym || "");
};

/** Read-only view of a client's CV/Bewerbungsprofil — for Coaches/Dozenten/Admin/Manager. */
export default function CvProfileReadOnly({ clientId, url }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(url || `/clients/${clientId}/cv-profile`);
        setProfile(data || {});
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
      finally { setLoading(false); }
    })();
  }, [clientId]);

  if (loading) return <div className="text-sm text-slate-500 py-6 text-center">Lädt CV-Profil…</div>;

  const p = profile || {};
  const hasAnything = p.headline || p.summary || p.phone || p.location || (p.skills || []).length
    || (p.work_experience || []).length || (p.education || []).length
    || (p.certifications || []).length || (p.languages || []).length || (p.desired_jobs || []).length;

  return (
    <div className="border border-slate-200 bg-white p-6" data-testid="cv-profile-readonly">
      <div className="mb-6">
        <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-1.5">
          <UserCircle size={11} strokeWidth={1.5} /> Bewerbungsprofil (nur lesend)
        </div>
        <div className="font-heading text-xl font-light tracking-tighter text-slate-900">CV / Bewerbungsprofil des Kunden</div>
      </div>

      {!hasAnything ? (
        <div className="text-sm text-slate-400 italic text-center py-10" data-testid="cv-readonly-empty">
          Der Kunde hat noch kein CV-Profil ausgefüllt.
        </div>
      ) : (
        <div className="space-y-5">
          {/* Photo + basics */}
          <div className="grid grid-cols-1 md:grid-cols-[100px_1fr] gap-4">
            <div className="w-24 h-32 bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden">
              {p.photo_base64 ? (
                <img src={p.photo_base64} alt="Portrait" className="w-full h-full object-cover" data-testid="cv-readonly-photo" />
              ) : (
                <Camera size={20} strokeWidth={1.2} className="text-slate-400" />
              )}
            </div>
            <div>
              {p.headline && <div className="text-sm font-medium text-slate-900" data-testid="cv-readonly-headline">{p.headline}</div>}
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600 mt-1">
                {p.phone && <span>{p.phone}</span>}
                {p.location && <span>{p.location}</span>}
              </div>
              <div className="flex flex-wrap gap-3 mt-1.5 text-xs">
                {[["Portfolio", p.portfolio_url], ["LinkedIn", p.linkedin_url], ["Xing", p.xing_url], ["Instagram", p.instagram_url], ["Website", p.website_url]]
                  .filter(([, v]) => v)
                  .map(([label, v]) => (
                    <a key={label} href={v} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[#00807E] hover:underline">
                      <LinkIcon size={10} strokeWidth={1.5} /> {label}
                    </a>
                  ))}
              </div>
              {p.summary && <p className="text-sm text-slate-700 leading-relaxed mt-2" data-testid="cv-readonly-summary">{p.summary}</p>}
            </div>
          </div>

          {(p.desired_jobs || []).length > 0 && (
            <ReadOnlyTags label="Gesuchte Positionen / Berufe" values={p.desired_jobs} testid="cv-readonly-desired" />
          )}
          {(p.skills || []).length > 0 && (
            <ReadOnlyTags label="Fähigkeiten / Qualifikationen" values={p.skills} testid="cv-readonly-skills" />
          )}

          {(p.work_experience || []).length > 0 && (
            <div data-testid="cv-readonly-exp">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Berufserfahrung</div>
              <div className="space-y-3">
                {p.work_experience.map((e, i) => (
                  <div key={i} className="border-l-2 border-slate-200 pl-3">
                    <div className="text-sm font-medium text-slate-900">{e.title}</div>
                    <div className="text-xs text-slate-500">
                      {[e.company, e.location].filter(Boolean).join(" · ")}
                      {(e.start || e.end || e.current) && `  ·  ${fmtMonthYearDe(e.start) || ""} – ${e.current ? "heute" : fmtMonthYearDe(e.end) || ""}`}
                    </div>
                    {e.description && <div className="text-xs text-slate-700 mt-1 whitespace-pre-line">{e.description}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(p.education || []).length > 0 && (
            <div data-testid="cv-readonly-edu">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Ausbildung / Weiterbildung</div>
              <div className="space-y-2">
                {p.education.map((ed, i) => (
                  <div key={i} className="border-l-2 border-slate-200 pl-3">
                    <div className="text-sm font-medium text-slate-900">{ed.degree}</div>
                    <div className="text-xs text-slate-500">
                      {[ed.institution, (ed.start || ed.end) ? `${fmtMonthYearDe(ed.start) || ""} – ${fmtMonthYearDe(ed.end) || ""}` : null].filter(Boolean).join(" · ")}
                    </div>
                    {ed.description && <div className="text-xs text-slate-700 mt-1 whitespace-pre-line">{ed.description}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(p.certifications || []).length > 0 && (
            <div data-testid="cv-readonly-cert">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Zertifikate</div>
              <div className="flex flex-wrap gap-1.5">
                {p.certifications.map((c, i) => (
                  <span key={i} className="text-xs px-2 py-1 bg-slate-100 border border-slate-200">
                    {[c.name, c.issuer, c.year].filter(Boolean).join(" · ")}
                  </span>
                ))}
              </div>
            </div>
          )}

          {(p.languages || []).length > 0 && (
            <div data-testid="cv-readonly-lang">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Sprachen</div>
              <div className="flex flex-wrap gap-1.5">
                {p.languages.map((l, i) => (
                  <span key={i} className="text-xs px-2 py-1 bg-slate-100 border border-slate-200">
                    {l.name}{l.level ? ` (${l.level})` : ""}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ReadOnlyTags({ label, values, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {values.map((v, i) => (
          <span key={i} className="text-xs px-2 py-1 bg-slate-100 border border-slate-200">{v}</span>
        ))}
      </div>
    </div>
  );
}
